import {
  CreditCard,
  HelpCircle,
  Play,
  Target,
  Trophy,
  UserPlus,
  Wallet
} from "lucide-react";
import { useEffect, useState } from 'react';
import { GET_CONTACT } from '../../service/admin.service';
import Footer from '../Footer/Footer';
import Navbar from '../Navbar/Navbar';

const HowToPlay = () => {
  const [getData, setgetData] = useState([]);

  const getResponseData = async () => {
    const res = await GET_CONTACT();
    setgetData(res.data[0]);
  };
  useEffect(() => {
    getResponseData();
  }, []);

  const steps = [
    {
      id: 1,
      icon: UserPlus,
      title: "Create An Account",
      color: "tw-bg-blue-100 tw-text-blue-600",
      details: [
        "Open The Laksh365 App Or Visit Our Website.",
        "Register Using Your Mobile Number And Set A Secure Password.",
        "Verify Your Number Using The OTP Sent To You.",
      ]
    },
    {
      id: 2,
      icon: Wallet,
      title: "Add Funds To Your Wallet",
      color: "tw-bg-green-100 tw-text-green-600",
      details: [
        "Go To The Add Funds Section.",
        "Select A Payment Method And Complete The Transaction.",
        "Funds Appear Instantly In Your Wallet — You're Ready To Play.",
      ]
    },
    {
      id: 3,
      icon: Target,
      title: "Choose A Market & Game",
      color: "tw-bg-purple-100 tw-text-purple-600",
      details: [
        "Select From Available Markets (For Example: Supreme Night, Morning, Etc.) And Pick The Game Type You Prefer:",
        "Open / Close — Pick A Single Digit Or Two-Digit Number.",
        "Sangam — Combination Plays With Higher Payouts.",
        "Custom Games — Special Formats Available On The App.",
      ]
    },
    {
      id: 4,
      icon: Play,
      title: "Place Your Bet",
      color: "tw-bg-teal-100 tw-text-teal-600",
      details: [
        "Enter The Number You Want To Bet On.",
        "Choose The Bet Type (Open, Close, Sangam, Etc.).",
        "Enter Your Stake (The Amount You Want To Bet).",
        "Confirm The Bet — It Will Be Recorded Immediately.",
        "Tip: Double-Check Market Timing And Your Selected Number Before Confirming. Bets Cannot Be Changed After Confirmation.",
      ]
    },
    {
      id: 5,
      icon: Trophy,
      title: "Result & Payouts",
      color: "tw-bg-yellow-100 tw-text-yellow-600",
      details: [
        "When Results Are Declared:",
        "Winning Bets Are Credited Automatically To Your Wallet.",
        "Payout Multiples Depend On The Market And Bet Type. Check The Payout Table In The App For Exact Rates.",
      ]
    },
    {
      id: 6,
      icon: CreditCard,
      title: "Withdraw Funds",
      color: "tw-bg-pink-100 tw-text-pink-600",
      details: [
        "Go To The Withdraw Section In Your Account.",
        "Enter Bank Details And The Amount To Withdraw.",
        "Submit The Withdrawal Request. Processing Times May Vary.",
      ]
    },
  ];

  return (
    <>
      <Navbar />

      <div className="tw-py-10 tw-px-4 md:tw-px-8 lg:tw-px-12 tw-min-h-screen lg:tw-mt-16 tw-mt-10">
        <div className="tw-max-w-6xl tw-mx-auto">
          {/* Header */}
          <div className="tw-text-center tw-mb-10">
            <h1 className="tw-text-3xl md:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-2">
              How To Play
            </h1>
            <p className="tw-text-gray-500 tw-text-sm md:tw-text-base">
              Follow these simple steps and start playing today.
            </p>
          </div>

          {/* Welcome Card */}
          <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-p-5 md:tw-p-6 tw-mb-8">
            <h2 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-3">
              Welcome To Laksh365
            </h2>
            <p className="tw-text-gray-700 tw-leading-relaxed tw-text-sm md:tw-text-base">
              Play Safely And Enjoy A Smooth Matka Experience. Follow These Simple Steps To Start Placing Bets And Winning.
            </p>
          </div>

          {/* Steps Grid */}
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-2 tw-gap-4 md:tw-gap-5">
            {steps.map((step) => {
              const IconComponent = step.icon;
              return (
                <div
                  key={step.id}
                  className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-p-4 md:tw-p-5 hover:tw-shadow-md tw-transition-shadow"
                >
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <div className={`tw-w-10 tw-h-10 md:tw-w-11 md:tw-h-11 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${step.color}`}>
                      <IconComponent className="tw-w-5 tw-h-5 md:tw-w-6 md:tw-h-6" />
                    </div>

                    <div className="tw-flex-1">
                      <h3 className="tw-text-base md:tw-text-lg tw-font-bold tw-text-gray-900 tw-mb-2">
                        {step.id}. {step.title}
                      </h3>
                      <ul className="tw-space-y-1 tw-text-gray-700 tw-text-xs md:tw-text-sm">
                        {step.details.map((detail, index) => (
                          <li key={index} className="tw-leading-relaxed">
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Need Help Section */}
          <div className="tw-bg-[#1e6ebe] tw-rounded-2xl tw-shadow-lg tw-p-5 md:tw-p-6 tw-mt-7">
            <div className="tw-flex tw-items-start tw-gap-3">
              <div className="tw-w-10 tw-h-10 md:tw-w-11 md:tw-h-11 tw-bg-white tw-bg-opacity-20 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                <HelpCircle className="tw-w-5 tw-h-5 md:tw-w-6 md:tw-h-6 tw-text-white" />
              </div>
              <div className="tw-flex-1">
                <h3 className="tw-text-lg md:tw-text-xl tw-font-bold tw-text-white tw-mb-1">
                  Need Help?
                </h3>
                <p className="tw-text-white tw-text-opacity-90 tw-leading-relaxed tw-text-xs md:tw-text-sm">
                  Visit The Help & Support Section Or Contact Our 24/7 Support Team Through The App's Chat.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div id="scroll-down"></div>
      <Footer />
    </>
  );
};

export default HowToPlay;